/**
 * TransactionExportDialog — [Sprint-F]
 *
 * A rich export dialog specific to transactions. Shows a preview summary of
 * what will be exported (count, date range, income/expenses) and lets the user
 * refine filters, pick a format (CSV / JSON), then download.
 *
 * Defaults to the current active FilterState from the Transactions page.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useCurrency } from '@/hooks/useCurrency';
import { useAppDispatch } from '@/features/hooks';
import { exportTransactions } from '@/features/transactions/transactionsSlice';
import type { FilterState } from '@/lib/filters';
import { getDateRangeFromFilter } from '@/lib/filters';
import type { Account, Category, Transaction } from '@/lib/types';
import {
  Download,
  Loader2,
  FileSpreadsheet,
  FileJson,
  CalendarIcon,
  Info,
} from 'lucide-react';

interface TransactionExportDialogProps {
  /** Current FilterState from the Transactions page — used as defaults */
  initialFilters?: FilterState;
  /** All transactions currently loaded (for preview summary) */
  transactions: Transaction[];
  accounts: Account[];
  categories: Category[];
  /** Custom trigger element; defaults to an Export button */
  trigger?: React.ReactNode;
}

type ExportFormat = 'csv' | 'json';
type ExportType = 'income' | 'expense' | 'transfer';
const ALL_TYPES: ExportType[] = ['income', 'expense', 'transfer'];

export function TransactionExportDialog({
  initialFilters,
  transactions,
  accounts,
  categories,
  trigger,
}: TransactionExportDialogProps) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const { fmt } = useCurrency();

  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // ── Local export filters (defaulting to page filters) ──────────────────
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<ExportType[]>([...ALL_TYPES]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');

  // Reset local state to match page filters when dialog opens
  useEffect(() => {
    if (!open) return;
    const f = initialFilters ?? { period: '30d' as const };
    const range = getDateRangeFromFilter(f.period, f.dateFrom, f.dateTo);
    setDateFrom(range.dateFrom ? range.dateFrom.slice(0, 10) : '');
    setDateTo(range.dateTo ? range.dateTo.slice(0, 10) : '');
    setSelectedTypes(f.type ? [f.type] : [...ALL_TYPES]);
    setSelectedAccountIds(f.accountIds ?? []);
    setSelectedCategoryIds(f.categoryIds ?? []);
    setExportFormat('csv');
  }, [open, initialFilters]);

  // ── Preview summary computed from loaded transactions & local filters ──
  const summary = useMemo(() => {
    let filtered = transactions;

    if (dateFrom) {
      const d = new Date(dateFrom);
      filtered = filtered.filter((t) => new Date(t.date) >= d);
    }
    if (dateTo) {
      const d = new Date(dateTo);
      d.setHours(23, 59, 59, 999);
      filtered = filtered.filter((t) => new Date(t.date) <= d);
    }
    if (selectedTypes.length > 0 && selectedTypes.length < ALL_TYPES.length) {
      filtered = filtered.filter((t) => selectedTypes.includes(t.type as ExportType));
    }
    if (selectedAccountIds.length > 0) {
      const set = new Set(selectedAccountIds);
      filtered = filtered.filter((t) => set.has(t.accountId));
    }
    if (selectedCategoryIds.length > 0) {
      const set = new Set(selectedCategoryIds);
      filtered = filtered.filter((t) => t.categoryId && set.has(t.categoryId));
    }

    const income = filtered
      .filter((t) => t.type === 'income')
      .reduce((s, t) => s + t.amount, 0);
    const expenses = filtered
      .filter((t) => t.type === 'expense')
      .reduce((s, t) => s + t.amount, 0);

    const dates = filtered.map((t) => new Date(t.date).getTime());
    const minDate = dates.length ? new Date(Math.min(...dates)) : null;
    const maxDate = dates.length ? new Date(Math.max(...dates)) : null;

    const uniqueCategories = new Set(
      filtered.map((t) => t.categoryId).filter(Boolean)
    );

    return {
      count: filtered.length,
      income,
      expenses,
      minDate,
      maxDate,
      categoryCount: uniqueCategories.size,
    };
  }, [transactions, dateFrom, dateTo, selectedTypes, selectedAccountIds, selectedCategoryIds]);

  const toggleType = (type: ExportType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      await dispatch(
        exportTransactions({
          format: exportFormat,
          ...(dateFrom && { dateFrom: new Date(dateFrom).toISOString() }),
          ...(dateTo && { dateTo: new Date(dateTo).toISOString() }),
        })
      ).unwrap();
      toast({
        title: 'Export complete',
        description: `${exportFormat.toUpperCase()} file downloaded with ~${summary.count} transactions.`,
      });
      setOpen(false);
    } catch (err: unknown) {
      toast({
        title: 'Export failed',
        description: String(err),
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  }, [dispatch, exportFormat, dateFrom, dateTo, summary.count, toast]);

  const formatDate = (d: Date | null) =>
    d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" data-testid="txn-export-btn">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="txn-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Export Transactions
          </DialogTitle>
          <DialogDescription>
            Review summary and filters before exporting.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ── Summary Banner ─────────────────────────────────────────── */}
          <div className="rounded-lg border bg-muted/40 p-4 space-y-3" data-testid="export-summary">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Export Preview</span>
              <Badge variant="secondary" className="tabular-nums">
                {summary.count} transaction{summary.count !== 1 ? 's' : ''}
              </Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Date range</p>
                <p className="font-medium tabular-nums">
                  {formatDate(summary.minDate)} – {formatDate(summary.maxDate)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Categories</p>
                <p className="font-medium">{summary.categoryCount}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Income</p>
                <p className="font-medium tabular-nums text-green-600 dark:text-green-400">
                  {fmt(summary.income)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Expenses</p>
                <p className="font-medium tabular-nums text-red-600 dark:text-red-400">
                  {fmt(summary.expenses)}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* ── Date Range Filter ──────────────────────────────────────── */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Date Range</Label>
              <Badge variant="outline" className="text-xs">Optional</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="export-from" className="text-xs">From</Label>
                <Input
                  id="export-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="export-to" className="text-xs">To</Label>
                <Input
                  id="export-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* ── Type Filter ────────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Transaction Types</Label>
            <div className="flex gap-3">
              {ALL_TYPES.map((type) => (
                <div key={type} className="flex items-center gap-2">
                  <Checkbox
                    id={`export-type-${type}`}
                    checked={selectedTypes.includes(type)}
                    onCheckedChange={() => toggleType(type)}
                  />
                  <Label htmlFor={`export-type-${type}`} className="text-sm capitalize cursor-pointer">
                    {type}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* ── Account Filter ─────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Account</Label>
            <Select
              value={selectedAccountIds[0] ?? 'all'}
              onValueChange={(v) => setSelectedAccountIds(v === 'all' ? [] : [v])}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All accounts</SelectItem>
                {accounts.map((acc) => (
                  <SelectItem key={acc.id} value={acc.id}>
                    {acc.emoji ? `${acc.emoji} ` : ''}{acc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ── Category Filter ────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Category</Label>
            <Select
              value={selectedCategoryIds[0] ?? 'all'}
              onValueChange={(v) => setSelectedCategoryIds(v === 'all' ? [] : [v])}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* ── Format Selector ────────────────────────────────────────── */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup
              value={exportFormat}
              onValueChange={(v) => setExportFormat(v as ExportFormat)}
              className="flex gap-4"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="csv" id="format-csv" />
                <Label htmlFor="format-csv" className="flex items-center gap-1.5 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4 text-green-600" />
                  CSV
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="json" id="format-json" />
                <Label htmlFor="format-json" className="flex items-center gap-1.5 cursor-pointer">
                  <FileJson className="h-4 w-4 text-blue-600" />
                  JSON
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3 shrink-0" />
              CSV is best for spreadsheets; JSON preserves full data structure.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || summary.count === 0}
            data-testid="export-confirm-btn"
          >
            {exporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            Export {summary.count} Transaction{summary.count !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
